import React, { useEffect, useMemo, useRef } from 'react';
import { Icon } from '../../../components/ui/Icon/Icon';
import { FileDropzone } from '../FileDropzone/FileDropzone';
import { FormSection } from '../FormSection/FormSection';
import styles from './SupportForm.module.scss';

import { Field, FieldRow, TextArea, TextInput } from '../../../components/ui/FormControls';
import { SearchSelect } from '../../../components/ui/SearchSelect/SearchSelect';
import type { SearchSelectOption } from '../../../components/ui/SearchSelect/SearchSelect';

import type { AttachedFile, FormErrors, SelectOption, SupportFormValues } from '../../../types/support';
import {
  categoryOptions,
  DESCRIPTION_MAX,
  projectOptions as defaultProjectOptions,
} from '../../../entities/support/model/constants';
import { useAppSelector } from '../../../app/hooks';
import { selectUser } from '../../../entities/session/model/slice';
import { userName } from '../../../types/user';

const toSearchOptions = (options: SelectOption[]): SearchSelectOption[] =>
  options.map((option) => ({ id: option.value, name: option.label }));

interface SupportFormProps {
  values: SupportFormValues;
  errors: FormErrors;
  onChange: <K extends keyof SupportFormValues>(key: K, value: SupportFormValues[K]) => void;
  files: AttachedFile[];
  onAddFiles: (files: FileList | File[]) => void;
  onRemoveFile: (id: string) => void;
  projectOptions?: SelectOption[];
  categoryOptions?: SelectOption[];
}

export function SupportForm({
  values,
  errors,
  onChange,
  files,
  onAddFiles,
  onRemoveFile,
  projectOptions = defaultProjectOptions,
  categoryOptions: categoryOptionsProp = categoryOptions,
}: SupportFormProps) {
  const user = useAppSelector(selectUser);

  const projects = useMemo(() => toSearchOptions(projectOptions), [projectOptions]);
  const categories = useMemo(() => toSearchOptions(categoryOptionsProp), [categoryOptionsProp]);

  const profileName = user ? userName(user) : '';
  const profileEmail = user?.email ?? '';

  const userKey = user?.id ?? profileEmail;

  /* undefined, а не null: тогда первый проход эффекта тоже считается
     сменой учётной записи и почта подставляется поверх любого значения. */
  const knownUserKey = useRef<typeof userKey | undefined>(undefined);

  useEffect(() => {
    if (!user || !profileEmail) return;

    const userChanged = knownUserKey.current !== userKey;
    knownUserKey.current = userKey;

    /* Подставляем профильный адрес при первом рендере и при смене учётки.
       Дальше поле принадлежит пользователю — эффект в него не лезет. */
    if (userChanged) {
      onChange('email', profileEmail);
    }

    /* Имя редактировать нельзя — держим его равным профилю всегда. */
    if (profileName !== values.name) {
      onChange('name', profileName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey, profileEmail, profileName]);

  return (
    <section className={styles.panel}>
      <header className={styles.head}>
        <span className={styles.headIcon}>
          <Icon name="headset" size={16} strokeWidth={1.7} />
        </span>
        <div>
          <h2>Новое обращение</h2>
          <p>Чем подробнее описание, тем быстрее инженер найдёт причину</p>
        </div>
      </header>

      <div className={styles.body}>
        <FormSection step={1} title="Контакт для ответа">
          <FieldRow>
            <Field
              htmlFor="email"
              label="Эл. почта"
              required
              hint="Подставлена почта из профиля — при необходимости измените. На этот адрес придёт номер обращения и ответ инженера"
              error={errors.email}
            >
              <TextInput
                id="email"
                type="email"
                autoComplete="off"
                placeholder="name@company.com"
                value={values.email}
                invalid={Boolean(errors.email)}
                onChange={(e) => onChange('email', e.target.value)}
              />
            </Field>

            <Field
              htmlFor="name"
              label="Имя"
              required
              hint="Берётся из профиля, изменить здесь нельзя"
              error={errors.name}
            >
              <TextInput
                id="name"
                autoComplete="name"
                placeholder="Иванов Иван Иванович"
                value={profileName}
                readOnly
                invalid={Boolean(errors.name)}
              />
            </Field>
          </FieldRow>
        </FormSection>

        <FormSection step={2} title="Где возникла проблема">
          <FieldRow>
            <Field
              htmlFor="project"
              label="Проект"
              required
              hint="Если проблема общая — выберите «Другое»"
              error={errors.project}
            >
              <SearchSelect
                id="project"
                placeholder="Выберите проект"
                options={projects}
                value={values.project}
                invalid={Boolean(errors.project)}
                onChange={(id) => onChange('project', id)}
              />
            </Field>

            <Field
              htmlFor="category"
              label="Раздел системы"
              required
              hint="Помогает сразу направить заявку нужному инженеру"
              error={errors.category}
            >
              <SearchSelect
                id="category"
                placeholder="Выберите раздел"
                options={categories}
                value={values.category}
                invalid={Boolean(errors.category)}
                onChange={(id) => onChange('category', id)}
              />
            </Field>
          </FieldRow>
        </FormSection>

        <FormSection step={3} title="Суть обращения">
          <FieldRow layout="single">
            <Field
              htmlFor="description"
              label="Описание"
              required
              hint="Первая строка станет темой обращения. Укажите время, когда это случилось, и номер проекта или позиции, если они известны"
              error={errors.description}
              counter={{ current: values.description.length, max: DESCRIPTION_MAX }}
            >
              <TextArea
                id="description"
                maxLength={DESCRIPTION_MAX}
                placeholder="Что вы делали, что ожидали увидеть и что произошло вместо этого. Если есть текст ошибки — вставьте его целиком."
                value={values.description}
                invalid={Boolean(errors.description)}
                onChange={(e) => onChange('description', e.target.value)}
              />
            </Field>
          </FieldRow>
        </FormSection>

        <FormSection step={4} title="Файлы" optional>
          <FileDropzone files={files} onAdd={onAddFiles} onRemove={onRemoveFile} />
        </FormSection>
      </div>
    </section>
  );
}